import { motion } from 'framer-motion'
import { LCARSPanel, LCARSBar } from '@enterpress/holomatrix'
import { 
  MessageSquare, 
  Wrench, 
  Package
} from 'lucide-react'
import { useTranslation } from '../i18n/index'

/**
 * Features Section - The Ready Room (Agentic Admin Panel)
 * 
 * Showcases the three operational decks of the Ready Room:
 * - Bridge Deck: Strategic planning with AI
 * - Engineering Deck: Live build visibility
 * - Cargo Bay: Content management
 */

export function Features() {
  const { t } = useTranslation()

  const DECKS = [
    {
      icon: MessageSquare,
      title: t('features.bridgeDeck'),
      description: t('features.bridgeDesc'),
      color: 'cyan' as const,
      highlight: t('features.bridgeHighlight'),
    },
    {
      icon: Wrench,
      title: t('features.engineeringDeck'),
      description: t('features.engineeringDesc'),
      color: 'amber' as const,
      highlight: t('features.engineeringHighlight'),
    },
    {
      icon: Package,
      title: t('features.cargoBay'),
      description: t('features.cargoDesc'),
      color: 'purple' as const,
      highlight: t('features.cargoHighlight'),
    },
  ]

  return (
    <section id="features" className="py-24 px-6 relative">
      {/* Background accent */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[rgba(255,179,71,0.02)] to-transparent pointer-events-none" />
      
      <div className="max-w-6xl mx-auto relative">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <LCARSBar color="amber" size="md" className="w-20" />
            <h2 className="text-sm font-bold tracking-[4px] uppercase text-[#FFB347]">
              {t('features.title')}
            </h2>
            <LCARSBar color="amber" size="md" className="w-20" />
          </div>
          <h3 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t('features.subtitle')}
          </h3>
          <p className="text-lg text-[rgba(232,244,248,0.6)] max-w-2xl mx-auto">
            {t('features.description')}
          </p>
        </motion.div>

        {/* Deck cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {DECKS.map((deck, index) => (
            <motion.div
              key={deck.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <LCARSPanel 
                title={deck.title} 
                color={deck.color}
                className="h-full"
              >
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className={`
                      w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0
                      ${deck.color === 'cyan' && 'bg-[rgba(0,212,255,0.15)] text-[#00D4FF]'}
                      ${deck.color === 'amber' && 'bg-[rgba(255,179,71,0.15)] text-[#FFB347]'}
                      ${deck.color === 'purple' && 'bg-[rgba(157,140,255,0.15)] text-[#9D8CFF]'}
                    `}>
                      <deck.icon size={24} />
                    </div>
                    <div className="flex-1">
                      <div className={`
                        text-xs font-bold tracking-wider uppercase mb-2
                        ${deck.color === 'cyan' && 'text-[#00D4FF]'}
                        ${deck.color === 'amber' && 'text-[#FFB347]'}
                        ${deck.color === 'purple' && 'text-[#9D8CFF]'}
                      `}>
                        {deck.highlight}
                      </div>
                      <p className="text-[rgba(232,244,248,0.7)] leading-relaxed text-sm">
                        {deck.description}
                      </p>
                    </div>
                  </div>
                </div>
              </LCARSPanel>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  )
}
